import { Mail } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { Alert, Button, EmptyState, ErrorState, FullPageSpinner, PageHeader, Section } from "@/components/ui";
import { useOrganization } from "@/auth/organization";
import { missingPermissionReason } from "@/lib/access";
import { emailApi, useMailboxOverview, type EmailAvailability, type EmailItem } from "@/lib/mailboxes";
import { formatDate } from "@/lib/format";

import { useEmailMutation } from "./actions";
import { CreateMailboxDialog } from "./dialogs";
import { MailboxCard } from "./MailboxCard";

/** Where to go when the reason a mailbox can't be created is the plan, not the person's access. */
export function planBlocked(
  a: Pick<EmailAvailability, "mailbox_limit" | "mailboxes_used" | "create_blocked_reason">,
) {
  return a.create_blocked_reason !== null && (a.mailbox_limit <= 0 || a.mailboxes_used >= a.mailbox_limit);
}

function RecentlyDeleted({
  items,
  onRecovered,
  windowDays,
}: {
  items: EmailItem[];
  onRecovered: (id: string) => void;
  windowDays: number;
}) {
  return (
    <Section title="Recently deleted" subtitle={`A deleted mailbox can be recovered for ${windowDays} days.`}>
      <ul className="divide-y divide-border">
        {items.map((item) => (
          <DeletedRow key={item.id} item={item} onRecovered={onRecovered} />
        ))}
      </ul>
    </Section>
  );
}

function DeletedRow({ item, onRecovered }: { item: EmailItem; onRecovered: (id: string) => void }) {
  const recover = useEmailMutation((org, _: void, key) => emailApi.act(org, item.id, "recover", key), {
    done: "Mailbox recovered",
    onResult: (r) => {
      if (r.outcome === "succeeded") onRecovered(item.id);
    },
  });
  const cap = item.actions.recover;
  return (
    <li className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <p className="text-sm font-medium break-all text-fg">{item.address}</p>
        {item.recoverable_until && (
          <p className="text-xs text-fg-subtle">Recoverable until {formatDate(item.recoverable_until)}</p>
        )}
        {recover.isError && (
          <p role="alert" className="text-xs text-red-700">
            {recover.error.message}
          </p>
        )}
      </div>
      <Button
        size="sm"
        variant="secondary"
        loading={recover.isPending}
        disabledReason={cap?.allowed === false ? (cap.reason ?? "That isn't possible right now.") : undefined}
        onClick={() => recover.mutate()}
      >
        Recover
      </Button>
    </li>
  );
}

export function MailboxesPage() {
  const overview = useMailboxOverview();
  const { canAny } = useOrganization();
  const [creating, setCreating] = useState(false);
  // Mailboxes deleted on this screen, so they can be recovered from here while the window is open.
  const [deleted, setDeleted] = useState<EmailItem[]>([]);

  if (overview.isPending) return <FullPageSpinner />;
  if (overview.isError) return <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />;
  const { availability, items } = overview.data;

  if (!availability.available) {
    return (
      <>
        <p className="mb-3 text-sm">
          <Link to="/email" className="font-semibold text-brand-700 underline">
            Back to Email (DNS status)
          </Link>
        </p>
        <PageHeader
          title="Mailboxes"
          description="Professional mailboxes, aliases and forwarding on your own domains."
        />
        <Section>
          <EmptyState
            icon={<Mail className="size-5" aria-hidden />}
            title="Professional email isn't available yet"
            description="We're setting up our email provider. When it's ready you'll create mailboxes here, and we'll tell you. Nothing is charged for email until then."
          />
        </Section>
      </>
    );
  }

  const mailboxes = items.filter((i) => i.kind === "mailbox" && i.state !== "deleted");
  // Deleted mailboxes the server still lets us recover, plus any deleted on this screen before the list reloaded.
  const recoverable = [
    ...items.filter((i) => i.kind === "mailbox" && i.state === "deleted"),
    ...deleted.filter((d) => !items.some((i) => i.id === d.id)),
  ];
  // Held on at least one domain folder is enough to ask; the server checks the folder of the domain chosen.
  const noAccess = canAny("email:manage") ? null : missingPermissionReason("email:manage");
  const blocked = availability.create_blocked_reason;

  return (
    <>
      <p className="mb-3 text-sm">
        <Link to="/email" className="font-semibold text-brand-700 underline">
          Back to Email (DNS status)
        </Link>
      </p>
      <PageHeader
        title="Mailboxes"
        description="Professional mailboxes, aliases and forwarding on your own domains."
        action={
          <div className="flex flex-col items-end gap-1">
            <Button disabledReason={noAccess ?? blocked ?? undefined} onClick={() => setCreating(true)}>
              Create mailbox
            </Button>
            <p className="text-xs text-fg-subtle">
              {availability.mailboxes_used} of {availability.mailbox_limit} mailboxes used on{" "}
              {availability.plan_name}
            </p>
          </div>
        }
      />

      <div className="flex flex-col gap-4">
        {availability.sandbox && (
          <Alert tone="warning">
            <strong>Test mode:</strong> these mailboxes are a stand-in and move no real mail.
          </Alert>
        )}
        {blocked && planBlocked(availability) && (
          <Alert tone="info">
            <p>{blocked}</p>
            <p className="mt-1">
              <Link to="/billing/plans" className="font-semibold underline">
                See plans and upgrade
              </Link>
            </p>
          </Alert>
        )}

        {mailboxes.length === 0 ? (
          <Section>
            <EmptyState
              icon={<Mail className="size-5" aria-hidden />}
              title="No mailboxes yet"
              description="Create hello@ on your domain, then add aliases or forwarding when you need them."
            />
          </Section>
        ) : (
          <ul className="flex flex-col gap-4" aria-label="Mailboxes">
            {mailboxes.map((mailbox) => (
              <li key={mailbox.id}>
                <MailboxCard
                  mailbox={mailbox}
                  all={items}
                  availability={availability}
                  onDeleted={(gone) => setDeleted((list) => [gone, ...list.filter((d) => d.id !== gone.id)])}
                />
              </li>
            ))}
          </ul>
        )}

        {recoverable.length > 0 && (
          <RecentlyDeleted
            items={recoverable}
            windowDays={availability.recovery_window_days}
            onRecovered={(id) => setDeleted((list) => list.filter((d) => d.id !== id))}
          />
        )}
      </div>

      {creating && <CreateMailboxDialog availability={availability} onClose={() => setCreating(false)} />}
    </>
  );
}
